(() => {
  const bind = root => root.querySelectorAll('[data-header-search]').forEach(panel => {
    if (panel.dataset.searchReady) return;
    const header = panel.closest('[data-site-header]');
    const toggle = header.querySelector('[data-header-search-toggle]');
    const input = panel.querySelector('[data-search-input]');
    const results = panel.querySelector('[data-search-results]');
    const defaults = panel.querySelector('[data-search-defaults]');
    const status = panel.querySelector('[data-search-status]');
    const all = panel.querySelector('[data-search-all]');
    const form = panel.querySelector('form');
    const labels = JSON.parse(panel.querySelector('[data-search-labels]').textContent);
    const events = new AbortController();
    const cache = new Map();
    let timer, request, revision = 0;
    const on = (target, event, callback) => target.addEventListener(event, callback, { signal: events.signal });
    const safeURL = (value, image = false) => {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, location.href);
        return /^https?:$/.test(url.protocol) && (image || url.origin === location.origin) ? url : null;
      } catch { return null; }
    };
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const fit = () => panel.style.setProperty('--search-available-height', `${Math.max(180, window.innerHeight - header.getBoundingClientRect().bottom - 12)}px`);
    const cancel = () => { revision++; clearTimeout(timer); request?.abort(); results.removeAttribute('aria-busy'); };
    const close = (restore = false) => {
      cancel(); panel.hidden = true; toggle.setAttribute('aria-expanded', 'false');
      if (restore) toggle.focus();
    };
    const showResults = data => {
      results.replaceChildren();
      let count = 0;
      for (const type of ['products', 'pages', 'articles']) {
        const list = element('ul', 'header-search__list');
        for (const item of (Array.isArray(data[type]) ? data[type] : []).slice(0, 6)) {
          if (!item || typeof item !== 'object') continue;
          const url = safeURL(item.url);
          if (!url || !item.title) continue;
          const link = element('a', 'header-search__result'); link.href = url.href;
          if (type === 'products' && item.image) {
            const imageURL = safeURL(item.image, true);
            if (imageURL) {
              imageURL.searchParams.set('width', '144');
              const image = element('img', ''); image.src = imageURL.href; image.alt = ''; image.width = 64; image.height = 64;
              image.loading = 'lazy'; link.append(image);
            }
          }
          const copy = element('span', ''); copy.append(element('strong', '', item.title));
          if (type === 'products') {
            const price = Number(item.price);
            if (item.price !== undefined && Number.isFinite(price)) copy.append(element('span', '', new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: panel.dataset.currency, currencyDisplay: 'code' }).format(price)));
            if (item.available === false) copy.append(element('span', '', labels.soldOut));
          }
          link.append(copy); const row = element('li', ''); row.append(link); list.append(row); count++;
        }
        if (list.children.length) {
          const group = element('section', 'header-search__group'); group.append(element('h3', '', labels[type]), list); results.append(group);
        }
      }
      status.textContent = count ? `${labels.count} ${count}` : labels.empty;
      results.hidden = false;
    };
    const search = async (query, id) => {
      request = new AbortController();
      const currentRequest = request;
      const timeout = setTimeout(() => currentRequest.abort(), 8000);
      try {
        let data = cache.get(query);
        if (!data) {
          const url = new URL(panel.dataset.suggestUrl, location.origin);
          url.search = new URLSearchParams({ q: query, 'resources[type]': 'product,page,article', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last' });
          const response = await fetch(url.href, { signal: currentRequest.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search unavailable');
          const payload = await response.json(); data = payload.resources?.results;
          if (!data) throw new Error('Invalid search response');
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(query, data);
        }
        if (id === revision && !panel.hidden) showResults(data);
      } catch {
        if (id === revision && !panel.hidden) { results.replaceChildren(); status.textContent = labels.error; }
      } finally {
        clearTimeout(timeout);
        if (id === revision) results.removeAttribute('aria-busy');
      }
    };
    const update = () => {
      cancel(); const query = input.value.trim();
      defaults.hidden = Boolean(query); results.hidden = true; all.hidden = !query;
      results.replaceChildren(); status.textContent = '';
      if (!query) return;
      const url = new URL(form.action, location.href);
      url.search = new URLSearchParams({ q: query, type: 'product,page,article', 'options[prefix]': 'last' });
      all.href = url.href; results.setAttribute('aria-busy', 'true'); status.textContent = labels.loading;
      const id = revision; timer = setTimeout(() => search(query, id), 200);
    };
    const open = () => {
      document.dispatchEvent(new CustomEvent('header:search-open'));
      const mobile = header.querySelector('[data-mobile-menu]');
      if (mobile && !mobile.hidden) header.querySelector('[data-mobile-menu-close]')?.click();
      panel.hidden = false; toggle.setAttribute('aria-expanded', 'true'); fit(); input.focus(); update();
    };
    on(toggle, 'click', event => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); panel.hidden ? open() : close(true);
    });
    on(toggle, 'keydown', event => { if (event.key === ' ') { event.preventDefault(); toggle.click(); } });
    on(input, 'input', event => { if (!event.isComposing) update(); });
    on(input, 'compositionend', update);
    on(panel.querySelector('[data-search-close]'), 'click', () => close(true));
    on(panel, 'keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(true); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const links = [...(defaults.hidden ? results : defaults).querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        if (document.activeElement === input || index >= 0) {
          event.preventDefault();
          if (event.key === 'ArrowUp' && index <= 0) input.focus();
          else links[Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))]?.focus();
        }
      }
    });
    on(document, 'pointerdown', event => { if (!panel.hidden && !panel.contains(event.target) && !toggle.contains(event.target)) close(); });
    on(document, 'focusin', event => { if (!panel.hidden && !panel.contains(event.target) && event.target !== toggle) close(); });
    on(document, 'header:menu-open', () => close());
    on(window, 'resize', () => { if (!panel.hidden) fit(); });
    on(window, 'scroll', () => { if (!panel.hidden) fit(); });
    on(document, 'shopify:section:unload', event => { if (event.target.contains(panel)) { cancel(); events.abort(); } });
    toggle.setAttribute('role', 'button'); panel.dataset.searchReady = 'true';
  });
  bind(document);
  document.addEventListener('shopify:section:load', event => bind(event.target));
})();
